import { HttpClient } from "@utils/index";
import type {
  ProviderResponse,
  ProviderViewModel,
  ProviderFilterParams,
  CreateProviderRequest,
  UpdateProviderRequest,
  CredentialResponse,
  CredentialViewModel,
  CreateCredentialRequest,
  UpdateCredentialRequest,
  PolicyResponse,
  PolicyViewModel,
  CreatePolicyRequest,
  UpdatePolicyRequest,
} from "@models/provider";
import type {
  ModelResponse,
  ModelViewModel,
  ModelFilterParams,
  ModelBasicInfoResponse,
  ModelBasicInfoViewModel,
  CreateModelRequest,
  UpdateModelRequest,
} from "@models/model";
import {
  buildPaginatedProvidersViewModel,
  buildProviderViewModel,
  buildCredentialViewModel,
  buildPolicyViewModel,
} from "@utils/builders/provider";
import {
  buildModelsBasicInfoViewModel,
  buildModelViewModel,
  buildPaginatedModelsViewModel,
  buildCreateModelApiRequest,
  buildUpdateModelApiRequest,
} from "@utils/builders/model";
import type { PaginatedResponse } from "@models/common";

const BASE_URL = import.meta.env.VITE_XPLATFORM_CORE_API_BASE_URL;

if (!BASE_URL) {
  throw new Error(
    "Missing required environment variable: VITE_XPLATFORM_CORE_API_BASE_URL. " +
      "Please check your .env file and ensure the XPlatform Core API URL is set.",
  );
}

interface CacheEntry<T> {
  expiresAt: number;
  data: T;
}

// Provider/model catalogs change rarely; cache reads briefly so the flow step
// editor doesn't refetch on every dialog open / provider switch. Mutations
// through this service bust the caches, so the provider management page (which
// reads and edits the same data) never serves its own stale writes.
const CACHE_TTL_MS = 5 * 60 * 1000;

class ProviderApi {
  private httpClient: HttpClient;
  private providersCache = new Map<
    string,
    CacheEntry<PaginatedResponse<ProviderViewModel>>
  >();
  private modelsBasicCache = new Map<
    string,
    CacheEntry<ModelBasicInfoViewModel[]>
  >();

  constructor() {
    this.httpClient = new HttpClient(`${BASE_URL}`);
  }

  private getCached<T>(cache: Map<string, CacheEntry<T>>, key: string): T | undefined {
    const entry = cache.get(key);
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
      cache.delete(key);
      return undefined;
    }
    return entry.data;
  }

  private setCached<T>(
    cache: Map<string, CacheEntry<T>>,
    key: string,
    data: T,
  ): void {
    cache.set(key, { expiresAt: Date.now() + CACHE_TTL_MS, data });
  }

  private invalidateProviderCaches(): void {
    this.providersCache.clear();
    this.modelsBasicCache.clear();
  }

  private invalidateModelCaches(): void {
    this.modelsBasicCache.clear();
  }

  async getProviders(
    filters?: ProviderFilterParams,
  ): Promise<PaginatedResponse<ProviderViewModel>> {
    const params = new URLSearchParams();
    if (filters?.limit !== undefined)
      params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined)
      params.append("offset", filters.offset.toString());
    if (filters?.status) params.append("status", filters.status);
    // Backend enum (ProviderTypeEnum) is upper-case LLM/OCR.
    if (filters?.providerType)
      params.append("provider_type", filters.providerType.toUpperCase());

    const queryString = params.toString();
    const url = `/providers${queryString ? `?${queryString}` : ""}`;

    const cacheKey = queryString;
    const cached = this.getCached(this.providersCache, cacheKey);
    if (cached) return { ...cached, items: [...cached.items] };

    const response = await this.httpClient.makeAuthenticatedRequest<
      PaginatedResponse<ProviderResponse>
    >(url, { method: "GET" });
    const result = buildPaginatedProvidersViewModel(response);
    this.setCached(this.providersCache, cacheKey, result);
    return result;
  }

  async getModelsBasicByProviderKey(
    providerKey: string,
  ): Promise<ModelBasicInfoViewModel[]> {
    const cached = this.getCached(this.modelsBasicCache, providerKey);
    if (cached) return [...cached];

    const url = `/providers/by-key/${encodeURIComponent(providerKey)}/models/basic`;
    const response = await this.httpClient.makeAuthenticatedRequest<
      ModelBasicInfoResponse[]
    >(url, { method: "GET" });
    const result = buildModelsBasicInfoViewModel(response);
    this.setCached(this.modelsBasicCache, providerKey, result);
    return result;
  }

  async getModelsByProvider(
    providerId: string,
    filters?: ModelFilterParams,
  ): Promise<PaginatedResponse<ModelViewModel>> {
    const params = new URLSearchParams();
    if (filters?.limit !== undefined)
      params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined)
      params.append("offset", filters.offset.toString());
    if (filters?.status) params.append("status", filters.status);

    const queryString = params.toString();
    const url = `/providers/${encodeURIComponent(providerId)}/models${queryString ? `?${queryString}` : ""}`;

    const response = await this.httpClient.makeAuthenticatedRequest<
      PaginatedResponse<ModelResponse>
    >(url, { method: "GET" });
    return buildPaginatedModelsViewModel(response);
  }

  async getModelDetailByName(modelName: string): Promise<ModelViewModel> {
    const url = `/providers/models/${encodeURIComponent(modelName)}/detail`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<ModelResponse>(url, {
        method: "GET",
      });
    return buildModelViewModel(response);
  }

  async createProvider(
    data: CreateProviderRequest,
  ): Promise<ProviderViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<ProviderResponse>(
        `/providers`,
        { method: "POST", body: JSON.stringify(data) },
      );
    this.invalidateProviderCaches();
    return buildProviderViewModel(response);
  }

  async updateProvider(
    providerId: string,
    data: UpdateProviderRequest,
  ): Promise<ProviderViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<ProviderResponse>(
        `/providers/${encodeURIComponent(providerId)}`,
        { method: "PUT", body: JSON.stringify(data) },
      );
    this.invalidateProviderCaches();
    return buildProviderViewModel(response);
  }

  async deleteProvider(providerId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest(
      `/providers/${encodeURIComponent(providerId)}`,
      { method: "DELETE" },
    );
    this.invalidateProviderCaches();
  }

  async getCredentialsByProvider(
    providerId: string,
  ): Promise<CredentialViewModel[]> {
    const response = await this.httpClient.makeAuthenticatedRequest<
      CredentialResponse[]
    >(`/providers/${encodeURIComponent(providerId)}/credentials`, {
      method: "GET",
    });
    return response.map(buildCredentialViewModel);
  }

  async createCredential(
    providerId: string,
    data: CreateCredentialRequest,
  ): Promise<CredentialViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<CredentialResponse>(
        `/providers/${encodeURIComponent(providerId)}/credentials`,
        { method: "POST", body: JSON.stringify(data) },
      );
    return buildCredentialViewModel(response);
  }

  async updateCredential(
    credentialId: string,
    data: UpdateCredentialRequest,
  ): Promise<CredentialViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<CredentialResponse>(
        `/providers/credentials/${encodeURIComponent(credentialId)}`,
        { method: "PUT", body: JSON.stringify(data) },
      );
    return buildCredentialViewModel(response);
  }

  async deleteCredential(credentialId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest(
      `/providers/credentials/${encodeURIComponent(credentialId)}`,
      { method: "DELETE" },
    );
  }

  async getPolicies(scopeKey?: string): Promise<PolicyViewModel[]> {
    const params = new URLSearchParams();
    if (scopeKey) params.append("scope_key", scopeKey);
    const qs = params.toString();
    const response = await this.httpClient.makeAuthenticatedRequest<
      PolicyResponse[]
    >(`/providers/policies${qs ? `?${qs}` : ""}`, { method: "GET" });
    return response.map(buildPolicyViewModel);
  }

  async createPolicy(data: CreatePolicyRequest): Promise<PolicyViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<PolicyResponse>(
        `/providers/policies`,
        { method: "POST", body: JSON.stringify(data) },
      );
    return buildPolicyViewModel(response);
  }

  async updatePolicy(
    policyId: string,
    data: UpdatePolicyRequest,
  ): Promise<PolicyViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<PolicyResponse>(
        `/providers/policies/${encodeURIComponent(policyId)}`,
        { method: "PUT", body: JSON.stringify(data) },
      );
    return buildPolicyViewModel(response);
  }

  async deletePolicy(policyId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest(
      `/providers/policies/${encodeURIComponent(policyId)}`,
      { method: "DELETE" },
    );
  }

  async createModel(
    providerId: string,
    data: CreateModelRequest,
  ): Promise<ModelViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<ModelResponse>(
        `/providers/${encodeURIComponent(providerId)}/models`,
        {
          method: "POST",
          body: JSON.stringify(
            buildCreateModelApiRequest({ ...data, providerId }),
          ),
        },
      );
    this.invalidateModelCaches();
    return buildModelViewModel(response);
  }

  async updateModel(
    modelId: string,
    data: UpdateModelRequest,
  ): Promise<ModelViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<ModelResponse>(
        `/providers/models/${encodeURIComponent(modelId)}`,
        {
          method: "PUT",
          body: JSON.stringify(buildUpdateModelApiRequest(data)),
        },
      );
    this.invalidateModelCaches();
    return buildModelViewModel(response);
  }

  async deleteModel(modelId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest(
      `/providers/models/${encodeURIComponent(modelId)}`,
      { method: "DELETE" },
    );
    this.invalidateModelCaches();
  }
}

export { ProviderApi };
export const providerApi = new ProviderApi();
